import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { ErrorResponse, Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { map, Observable } from 'rxjs'
import {
  CreateWebhook,
  UpdateWebhookPayload,
  Webhook,
  WebhookApi,
  WebhookListResult,
  WebhookLog,
  WebhookLogListParams,
  WebhookLogListResult,
  WebhookLogPaginatedResponse,
  WebhookPaginatedResponse
} from '../enums/webhooks.model'

const webhookMutationContext = () => new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)

/**
 * Returns the API error text from an HTTP error body, or `fallback`.
 * @param error {unknown}
 * @param fallback {string}
 * @returns {string}
 */
export function webhookHttpErrorMessage(error: unknown, fallback: string): string {
  const body = error instanceof HttpErrorResponse ? error.error : (error as { error?: unknown })?.error

  if (typeof body === 'string' && body.trim()) return body.trim()

  if (body && typeof body === 'object') {
    const record = body as ErrorResponse & { errors?: unknown; error?: unknown; message?: unknown }
    const fromErrors = _flattenApiFieldErrors(record.errors)
    if (fromErrors) return fromErrors

    const message = record.message
    if (typeof message === 'string' && message.trim()) return message.trim()
    if (Array.isArray(message)) {
      const joined = message.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).join(' ')
      if (joined) return joined
    }
    if (typeof record.error === 'string' && record.error.trim()) return record.error.trim()
  }

  return fallback
}

/**
 * Joins field-level API validation errors into a single message.
 * @param errors {unknown}
 * @returns {string | null}
 */
function _flattenApiFieldErrors(errors: unknown): string | null {
  if (typeof errors === 'string' && errors.trim()) return errors.trim()

  if (Array.isArray(errors)) {
    const parts = errors.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    return parts.length ? parts.join(' ') : null
  }

  if (errors && typeof errors === 'object') {
    const parts = Object.values(errors as Record<string, unknown>).flatMap((value) => {
      if (typeof value === 'string' && value.trim()) return [value.trim()]
      if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      return []
    })
    return parts.length ? parts.join(' ') : null
  }

  return null
}

export type WebhookListParams = {
  event_id?: number | null
  name?: string
  page: number
  per_page: number
}

@Injectable()
export class WebhookService {
  constructor(private http: HttpClient) {}

  getWebhooks(organizationId: number, params: WebhookListParams): Observable<WebhookListResult> {
    let httpParams = new HttpParams().set('page', params.page).set('per_page', params.per_page)

    httpParams = httpParams.set('event_id', params.event_id != null ? params.event_id : '')
    httpParams = httpParams.set('name', params.name?.trim() ?? '')

    return this.http.get<WebhookPaginatedResponse>(`${environment.integrationsApi}webhooks/${organizationId}`, { params: httpParams }).pipe(
      map((response) => ({
        items: response.data.map((webhook) => this._normalizeWebhook(webhook)),
        current_page: response.pagination.current_page,
        last_page: response.pagination.last_page
      }))
    )
  }

  createWebhook(payload: CreateWebhook): Observable<Webhook> {
    return this.http
      .post<Response<WebhookApi>>(`${environment.integrationsApi}webhooks`, payload, { context: webhookMutationContext() })
      .pipe(map((response) => this._normalizeWebhook(response.data)))
  }

  updateWebhook(webhookId: number, payload: UpdateWebhookPayload): Observable<Webhook> {
    return this.http
      .put<Response<WebhookApi>>(`${environment.integrationsApi}webhooks/${webhookId}`, payload, { context: webhookMutationContext() })
      .pipe(map((response) => this._normalizeWebhook(response.data)))
  }

  deleteWebhook(webhookId: number): Observable<void> {
    return this.http.delete<void>(`${environment.integrationsApi}webhooks/${webhookId}`)
  }

  getWebhookLogs(webhookId: number, params: WebhookLogListParams): Observable<WebhookLogListResult> {
    const httpParams = new HttpParams().set('page', params.page).set('per_page', params.per_page)

    return this.http.get<WebhookLogPaginatedResponse>(`${environment.integrationsApi}webhooks/logs/${webhookId}`, { params: httpParams }).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return {
            items: response,
            current_page: params.page,
            last_page: params.page
          }
        }

        return {
          items: response.data,
          current_page: response.pagination.current_page,
          last_page: response.pagination.last_page
        }
      })
    )
  }

  private _normalizeWebhook(webhook: WebhookApi): Webhook {
    const config = Array.isArray(webhook.config) ? undefined : webhook.config

    return {
      ...webhook,
      active: Boolean(webhook.active),
      config
    }
  }
}
